<script setup>
import { ref, computed, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import FormSelect from '../Atoms/FormSelect.vue'
import Alert from '../Atoms/Alert.vue'
import ModalBox from './ModalBox.vue'
import { sceneTemplates, cloneMediaPayload, uniqueMediaName, flowErrorMessage } from '@/components/Mediaflow/mediaflowMeta'
import { createFlowWithMedia } from '@/components/Mediaflow/createFlowWithMedia'

/**
 * New scene, optionally from a template. Templates reuse sources the
 * production already has (cameras, clips, pages from other scenes) and place
 * them; the scene is created with the existing endpoints (flow, then media).
 */
const props = defineProps({
	mediaflowId: {
		type: String,
		default: '',
		required: true,
	},
	canvas: {
		type: Object,
		default: () => ({ width: 1920, height: 1080 }),
	},
	/** `productionInputs()` — sources already used somewhere in the production. */
	inputs: {
		type: Array,
		default: () => [],
	},
	/** Returns a fresh Set of the source names already used in the production. */
	takenNames: {
		type: Function,
		default: () => new Set(),
	},
	/** Scenes so far, for the default "Scene N" name. */
	sceneCount: {
		type: Number,
		default: 0,
	},
})

const LAST_TEMPLATE_KEY = 'castr-mf-last-scene-template'

const { notify } = useNotification()

const isVisible = ref(false)
const error = ref(null)
const processing = ref(false)
const nameInput = ref(null)
const name = ref('')
const templateKey = ref('blank')
const picks = ref({})

const emit = defineEmits(['flow-added'])

const templates = computed(() => sceneTemplates(props.canvas))
const template = computed(() => templates.value.find((t) => t.key === templateKey.value) || templates.value[0])
const inputOptions = computed(() => props.inputs.map((i) => ({ label: i.media.name, value: i.key })))
const needsInputs = (t) => t.slots.length > 0
const templateDisabled = (t) => needsInputs(t) && props.inputs.length < Math.min(t.slots.length, 1)

const defaultName = computed(() => `Scene ${props.sceneCount + 1}`)
const selectTemplate = (t) => {
	if (templateDisabled(t)) return
	templateKey.value = t.key
	try { localStorage.setItem(LAST_TEMPLATE_KEY, t.key) } catch (e) { /* private mode */ }
	const next = {}
	t.slots.forEach((slot, i) => {
		// Main gets the first input, the second slot the next one (or none).
		const order = slot.key === 'main' ? 0 : 1
		next[slot.key] = picks.value[slot.key] || props.inputs[order]?.key || (i === 0 ? props.inputs[0]?.key : '') || ''
	})
	picks.value = next
	if (!name.value.trim() || name.value === defaultName.value || templates.value.some((x) => x.label === name.value)) name.value = t.key === 'blank' ? defaultName.value : t.label
}

const onReset = () => {
	name.value = ''
	templateKey.value = 'blank'
	picks.value = {}
	error.value = null
	processing.value = false
}

/** Open the modal, optionally on a template (from the Scenes dock "+" menu). */
const open = async (templateOrKey = null) => {
	onReset()
	isVisible.value = true
	let key = typeof templateOrKey === 'string' ? templateOrKey : templateOrKey?.key
	if (!key) {
		try { key = localStorage.getItem(LAST_TEMPLATE_KEY) } catch (e) { key = null }
	}
	const t = templates.value.find((x) => x.key === key && !templateDisabled(x)) || templates.value[0]
	selectTemplate(t)
	await nextTick()
	nameInput.value?.focus?.()
	nameInput.value?.$el?.querySelector?.('input')?.select?.()
}
// Kept for API compatibility with the parent (`addFlowModalRef.toggleSidebar()`).
const toggleSidebar = async (templateOrKey = null) => {
	if (isVisible.value) { isVisible.value = false; return }
	await open(templateOrKey)
}

/** Pickers list the main source first, whatever its layer. */
const pickerSlots = computed(() => [...template.value.slots].sort((a, b) => (a.key === 'main' ? -1 : b.key === 'main' ? 1 : 0)))
const slotsReady = computed(() => template.value.slots.every((slot) => slot.key !== 'main' || !!picks.value[slot.key]))
const valid = computed(() => !!name.value.trim() && slotsReady.value)

const createFlow = async () => {
	const sceneName = name.value.trim()
	if (!valid.value || processing.value) return
	processing.value = true
	error.value = null
	try {
		const taken = props.takenNames()
		const payloads = template.value.slots
			.map((slot) => {
				const input = props.inputs.find((i) => i.key === picks.value[slot.key])
				if (!input) return null
				return cloneMediaPayload(input.media, { name: uniqueMediaName(input.media.name, taken, sceneName), show: true, transform: { ...slot.transform } })
			})
			.filter(Boolean)
		// `kind` is accepted by the new engine and ignored by older ones.
		const newFlow = await createFlowWithMedia(props.mediaflowId, sceneName, { kind: 'layout' }, payloads)
		const withMedia = payloads.length > 0
		emit('flow-added', newFlow, { withMedia })
		notify({ group: 'success', text: `“${sceneName}” is in Preview` })
		isVisible.value = false
	} catch (err) {
		error.value = flowErrorMessage(err, 'Could not create the scene.')
	} finally {
		processing.value = false
	}
}

defineExpose({ toggleSidebar, open })
</script>

<template>
	<ModalBox v-model="isVisible" title="New scene" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3" @close="onReset">
		<form class="flex flex-col gap-3.5 px-5" @submit.prevent="createFlow">
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>

			<div>
				<span class="mb-2 block text-tiny font-medium text-surface-text-1">Start from</span>
				<div class="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Scene template">
					<button
						v-for="t in templates"
						:key="t.key"
						type="button"
						role="radio"
						:aria-checked="templateKey === t.key"
						class="flex flex-col items-start gap-1.5 rounded-lg p-2 text-left ring-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-40"
						:class="templateKey === t.key ? 'bg-surface-4 ring-main' : 'bg-surface-3 ring-transparent hover:ring-surface-6'"
						:disabled="templateDisabled(t)"
						:title="templateDisabled(t) ? 'Add a source to a scene first' : t.hint"
						:data-testid="`new-scene-template-${t.key}`"
						@click="selectTemplate(t)"
					>
						<span class="relative block aspect-video w-full overflow-hidden rounded bg-surface-1" aria-hidden="true">
							<span
								v-for="(box, i) in t.slots"
								:key="box.key"
								class="absolute rounded-[2px] ring-1 ring-surface-1"
								:class="box.key === 'main' ? 'bg-main-light' : 'bg-success'"
								:style="{ left: `${(box.transform.positionX / canvas.width) * 100}%`, top: `${(box.transform.positionY / canvas.height) * 100}%`, width: `${box.transform.scaleX}%`, height: `${box.transform.scaleY}%`, zIndex: t.slots.length - i }"
								:data-slot="box.key"
							/>
						</span>
						<span class="block w-full truncate text-xs font-medium text-surface-text-2">{{ t.label }}</span>
					</button>
				</div>
			</div>

			<FormInput
				id="add-flow-name"
				ref="nameInput"
				v-model="name"
				label="Scene name"
				placeholder="e.g. Wide camera, Interview, Be right back"
				:maxlength="120"
				data-testid="new-scene-name"
				@keydown.enter.prevent="createFlow"
			/>

			<div v-if="template.slots.length" class="grid gap-3" :class="template.slots.length > 1 ? 'sm:grid-cols-2' : ''">
				<FormSelect
					v-for="slot in pickerSlots"
					:key="slot.key"
					v-model="picks[slot.key]"
					:label="slot.key === 'main' ? slot.label : `${slot.label} (optional)`"
					:options="slot.key === 'main' ? inputOptions : [{ label: 'None', value: '' }, ...inputOptions]"
					:data-testid="`new-scene-slot-${slot.key}`"
				/>
			</div>
		</form>
		<template #footer>
			<div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-4 sm:flex-row sm:justify-end">
				<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="processing" @click="isVisible = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid" :is-loading="processing" data-testid="create-scene-submit" @click="createFlow">Create scene</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
